import Tracker from "../../../../prop-trackers/tracker.js";
import SinglePropTracker from "../../../../prop-trackers/single-prop-tracker.js";
import PropTracker from "../../../../prop-trackers/prop-tracker.js";
import ValueTracker from "../../../../prop-trackers/value-tracker.js";
import Mux from "../../../../prop-tracker-utils/mux.js";
import { Mapper, ValueMapper } from "../../../../prop-tracker-utils/mapper.js";

class ObjectMaterialMapper extends Tracker {
    constructor(objDataTracker, evtCtx, shlWrapCtx, shlEvtCtx) {
        super();

        const loadedDeps = shlWrapCtx.getWrapper({});
        const loadedDepEvents = shlEvtCtx.getEvents(loadedDeps);
        this._loadedDeps = loadedDeps;

        this._mapper = new Mapper(objDataTracker, () => new ObjectMapper(evtCtx, shlWrapCtx, loadedDeps, loadedDepEvents));
        this._mapper.listenNeedsUpdate(() => this.protected_needsUpdate());
    }

    trackMaterials(materials, materialEvents) {
        if (materials) {
            this._loadedDeps.materials = materials;
            this._loadedDeps.materialEvents = materialEvents;
        } else {
            delete this._loadedDeps.materials;
            delete this._loadedDeps.materialEvents;
        }
    }

    protected_startUpdate() {
        return this._mapper.startUpdate();
    }

    protected_finishUpdate() {
        this._mapper.finishUpdate();
    }
}

class ObjectMapper extends ValueMapper {
    constructor(evtCtx, shlWrapCtx, loadedDeps, loadedDepEvents) {
        super();

        this._evtCtx = evtCtx;
        this._loadedDeps = loadedDeps;
        this._loadedDepEvents = loadedDepEvents;

        this._depTracker = new PropTracker();

        this._materialKeyTracker = new SinglePropTracker();
        this._singleMaterialKeyTracker = new ValueTracker();
        this._multiMaterialKeyArrayTracker = new ValueTracker();
        this._multiMaterialKeyTracker = new PropTracker();
        this._singleMaterialMux = new Mux();
        this._multiMaterialMapper = new Mapper(this._multiMaterialKeyTracker, (key) => {
            if (key === "length") {
                return new IdentityMapper();
            } else {
                return new MaterialSlotMapper(this._loadedDeps, this._loadedDepEvents);
            }
        });
        this._multiMaterial = shlWrapCtx.getWrapper([]);
        this._multiMaterialTracker = new ValueTracker();

        this._valueTracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._depTracker.listenNeedsUpdate(needsUpdate);
        this._materialKeyTracker.listenNeedsUpdate(needsUpdate);
        this._singleMaterialMux.listenNeedsUpdate(needsUpdate);
        this._multiMaterialMapper.listenNeedsUpdate(needsUpdate);
    }

    setValue(data) {
        if (data) {
            this._depTracker.track(this._loadedDeps, this._loadedDepEvents);
            this._materialKeyTracker.track(data, this._evtCtx.getEvents(data), "material");
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._depTracker.track();
        this._materialKeyTracker.track();
    }

    protected_startUpdate() {
        this._depTracker.updateIfNeeded(({ props }) => {
            const { newValue: materials } = props.get("materials");
            const { newValue: matEvents } = props.get("materialEvents");

            this._singleMaterialMux.setSource(materials, materials && matEvents);
        });

        this._materialKeyTracker.updateIfNeeded(({ prop: { newValue } }) => {
            if (typeof newValue === "string") {
                this._singleMaterialKeyTracker.setValue(newValue);
            } else {
                this._singleMaterialKeyTracker.deleteValue();
            }

            if (Array.isArray(newValue)) {
                this._multiMaterialKeyArrayTracker.setValue(newValue);
            } else {
                this._multiMaterialKeyArrayTracker.deleteValue();
            }
        });

        this._singleMaterialKeyTracker.updateIfNeeded(({ prop: { newExists, newValue } }) => {
            this._singleMaterialMux.setKey(newExists ? newValue : undefined);
        });

        let multiMaterialModified = false;

        this._multiMaterialKeyArrayTracker.updateIfNeeded(({ prop: { newValue } }) => {
            this._multiMaterialKeyTracker.track(newValue, newValue ? this._evtCtx.getEvents(newValue) : undefined);

            multiMaterialModified = true;
            if (newValue) {
                this._multiMaterial.length = newValue.length;
                this._multiMaterialTracker.setValue(this._multiMaterial);
            } else {
                this._multiMaterial.length = 0;
                this._multiMaterialTracker.deleteValue(this._multiMaterial);
            }
        });

        this._multiMaterialMapper.updateIfNeeded(({ props }) => {
            for (const { key, newExists, newValue } of props.iterateModified()) {
                multiMaterialModified = true;

                if (newExists) {
                    this._multiMaterial[key] = newValue;
                } else if (key !== "length") {
                    delete this._multiMaterial[key];
                }
            }
        });

        if (this._singleMaterialMux.needsUpdate || multiMaterialModified) {
            this._singleMaterialMux.update(({ prop: { newValue: materialSingle } }) => {
                this._multiMaterialTracker.update(({ prop: { newValue: materialMulti } }) => {
                    if (materialSingle) {
                        this._valueTracker.setValue(materialSingle);
                    } else {
                        if (materialMulti && every(materialMulti)) {
                            this._valueTracker.setValue(materialMulti);
                        } else {
                            this._valueTracker.deleteValue();
                        }
                    }
                });
            });
        }

        return this._valueTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._valueTracker.finishUpdate();
    }
}

// Made this function because Array method .every does not check deleted properties
function every(array) {
    for (let i = 0; i < array.length; i++) {
        if (!array[i]) return false;
    }

    return true;
}

class IdentityMapper extends ValueMapper {
    constructor() {
        super();
        this._valueTracker = new ValueTracker();
        this._valueTracker.listenNeedsUpdate(() => this.protected_needsUpdate());
    }

    setValue(data) {
        if (data !== undefined) {
            this._valueTracker.setValue(data);
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._valueTracker.deleteValue();
    }

    protected_startUpdate() {
        return this._valueTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._valueTracker.finishUpdate();
    }
}

class MaterialSlotMapper extends ValueMapper {
    constructor(loadedDeps, loadedDepEvents) {
        super();

        this._loadedDeps = loadedDeps;
        this._loadedDepEvents = loadedDepEvents;

        this._depTracker = new PropTracker();
        this._mux = new Mux();

        this._depTracker.listenNeedsUpdate(() => this.protected_needsUpdate());
        this._mux.listenNeedsUpdate(() => this.protected_needsUpdate());
    }

    setValue(matKey) {
        if (typeof matKey === "string") {
            this._depTracker.track(this._loadedDeps, this._loadedDepEvents);
            this._mux.setKey(matKey);
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._depTracker.track();
        this._mux.setKey();
    }

    protected_startUpdate() {
        this._depTracker.updateIfNeeded(({ props }) => {
            const { newValue: materials } = props.get("materials");
            const { newValue: materialEvents } = props.get("materialEvents");

            this._mux.setSource(materials, materials && materialEvents);
        });

        return this._mux.startUpdate();
    }

    protected_finishUpdate() {
        this._mux.finishUpdate();
    }
}

export default ObjectMaterialMapper;
